<template>
    <v-autocomplete
        autocomplete="no"
        :value="value"
        :items="allOptions"
        :item-text="itemText"
        :item-value="itemValue"
        :label="label"
        :placeholder="placeholder"
        :return-object="returnObject"
        :clearable="clearable"
        :dense="dense"
        :outlined="outlined"
        :hide-details="hideDetails"
        :error="error"
        :error-messages="errorMessages"
        :disabled="disabled"
        :loading="searching || loading"
        :rules="rules"
        :prepend-icon="prependIcon"
        :multiple="multiple"
        :chips="chips"
        :small-chips="smallChips"
        :deletable-chips="deletableChips"
        :menu-props="{'offset-y': true, maxHeight: '200', nudgeBottom: 3}"
        :cache-items="cacheItems && !!asyncUrl"
        :auto-select-first="true"
        :search-input.sync="searchInput"
        :item-disabled="itemDisabled"
        @input="$emit('input', $event)"
        @change="$emit('change', $event)"
    >
        <!-- Item slot -->
        <template v-slot:item="data">
            <template v-if="typeof data.item !== 'object'">
                <v-list-item-content v-text="data.item"></v-list-item-content>
            </template>
            <template v-else>
                <v-list-item-avatar
                    v-if="getIcon(data.item)"
                    size="32px"
                    style="min-width: 32px; width: 32px; height: 32px; border-radius: 50%"
                    class="mr-4"
                    :color="data.item.color ? data.item.color : 'primary'"
                >
                    <v-icon class="fs18i" :color="data.item.color ? data.item.color : 'white'">{{ getIcon(data.item) }}</v-icon>
                </v-list-item-avatar>
                <v-list-item-avatar
                    v-else-if="data.item.avatar"
                    style="margin-right: 10px"
                >
                    <v-avatar
                        class="custom-avatar"
                        :size="34"
                        :style="{'background-image': 'url(' + data.item.avatar + ')'}"
                    ></v-avatar>
                </v-list-item-avatar>
                <v-list-item-content>
                    <v-list-item-title>{{ data.item[itemText] }}</v-list-item-title>
                    <v-list-item-subtitle v-if="searchKeys.length">
                        <span v-for="(key, k) in searchKeys" :key="k">{{ data.item[key] }}&nbsp;</span>
                    </v-list-item-subtitle>
                </v-list-item-content>
            </template>
        </template>

        <!-- Selection slot -->
        <template v-slot:selection="{ item, parent, selected, disabled: itemDisabled }">
            <v-chip v-if="chips"
                :small="smallChips"
                :close="deletableChips"
                :input-value="selected"
                :disabled="itemDisabled"
                @click:close="parent.selectItem(item)"
            >
                {{ item[itemText] }}
            </v-chip>
            <span v-else class="text-truncate" :style="{'max-width': selectionMaxWidth}">{{ item[itemText] }}</span>
        </template>

        <!-- No data slot -->
        <template v-slot:no-data>
            <v-list-item>
                <v-list-item-content>
                    <v-list-item-title v-if="searchInput && !searching">
                        Nessun risultato per "<strong>{{ searchInput }}</strong>".
                    </v-list-item-title>
                    <v-list-item-title v-else-if="asyncUrl && (!searchInput || searching)">
                        Cerca...
                    </v-list-item-title>
                    <v-list-item-title v-else>
                        Nessun risultato
                    </v-list-item-title>
                </v-list-item-content>
            </v-list-item>
        </template>
    </v-autocomplete>
</template>

<script>
export default {
    name: 'AsyncAutocomplete',

    props: {
        value: { default: null },
        asyncUrl: { type: String, default: null },
        items: { type: Array, default: () => [] },
        itemText: { type: String, default: 'text' },
        itemValue: { type: String, default: 'value' },
        searchKeys: { type: Array, default: () => [] },
        label: { type: String, default: '' },
        placeholder: { type: String, default: '' },
        returnObject: { type: Boolean, default: false },
        clearable: { type: Boolean, default: true },
        dense: { type: Boolean, default: true },
        outlined: { type: Boolean, default: true },
        hideDetails: { type: [Boolean, String], default: false },
        error: { type: Boolean, default: false },
        errorMessages: { default: () => [] },
        disabled: { type: Boolean, default: false },
        loading: { type: Boolean, default: false },
        multiple: { type: Boolean, default: false },
        minChars: { type: Number, default: 2 },
        debounceMs: { type: Number, default: 300 },
        rules: { type: Array, default: () => [] },
        cacheItems: { type: Boolean, default: true },
        prependIcon: { type: String, default: undefined },
        selectionMaxWidth: { type: String, default: '100%' },
        itemDisabled: { type: String, default: 'barred' },
        asyncParams: { type: Object, default: () => ({}) },
        chips: { type: Boolean, default: false },
        smallChips: { type: Boolean, default: false },
        deletableChips: { type: Boolean, default: false },
    },

    data() {
        return {
            searchInput: '',
            searching: false,
            asyncOptions: [],
            debounceTimer: null,
        }
    },

    computed: {
        allOptions() {
            if (!this.asyncUrl) return this.items

            const merged = [...this.items]
            this.asyncOptions.forEach(opt => {
                const key = this.itemValue
                if (!merged.find(m => m[key] === opt[key])) {
                    merged.push(opt)
                }
            })

            // Include current value(s) if return-object and not in list
            if (this.returnObject && this.value) {
                const key = this.itemValue
                if (Array.isArray(this.value)) {
                    this.value.forEach(v => {
                        if (v && typeof v === 'object' && !merged.find(m => m[key] === v[key])) {
                            merged.unshift(v)
                        }
                    })
                } else if (typeof this.value === 'object') {
                    if (!merged.find(m => m[key] === this.value[key])) {
                        merged.unshift(this.value)
                    }
                }
            }

            return merged
        }
    },

    watch: {
        searchInput(val) {
            if (!this.asyncUrl) return
            if (!val || val.length < this.minChars) return
            this.debouncedSearch(val)
        }
    },

    methods: {
        debouncedSearch(search) {
            if (this.debounceTimer) {
                clearTimeout(this.debounceTimer)
            }
            this.debounceTimer = setTimeout(() => {
                this.fetchOptions(search)
            }, this.debounceMs)
        },

        async fetchOptions(search) {
            this.searching = true
            try {
                const params = { search, ...this.asyncParams }
                const response = await this.axios.get(this.asyncUrl, { params })
                const options = response.data.options || []

                if (options.length + this.asyncOptions.length <= 50) {
                    this.asyncOptions = this.asyncOptions.concat(options)
                } else {
                    this.asyncOptions = options.slice(0, 50)
                }
            } catch (error) {
                console.error('AsyncAutocomplete search error:', error)
            } finally {
                this.searching = false
            }
        },

        getIcon(item) {
            if (typeof item.icon !== 'undefined' && item.icon) {
                return item.icon
            }
            if (item.extra_fields && typeof item.extra_fields === 'string') {
                try {
                    const parsed = JSON.parse(item.extra_fields)
                    if (parsed.icon) return parsed.icon
                } catch (e) {
                    // ignore
                }
            }
            return false
        }
    },

    beforeDestroy() {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer)
        }
    }
}
</script>
